import PropTypes from "prop-types";
import { useState, useMemo } from "react";
import { useTranslation } from "react-i18next";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faTimes,
  faChartPie,
  faChartBar,
  faChartLine,
  faBox,
  faCheckCircle,
  faPercent,
  faDollarSign,
  faStar,
  faCalendarAlt,
  faLayerGroup,
  faBuilding,
  faMapMarkerAlt,
} from "@fortawesome/free-solid-svg-icons";
import {
  PieChart,
  Pie,
  BarChart,
  Bar,
  LineChart,
  Line,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  ResponsiveContainer,
  Cell,
} from "recharts";
import Card from "../ui/Card";
import "../../style/DetailedViewPanel.css";
import "../../style/GlobalViewPanel.css";

const COLORS = [
  "#669d2e",
  "#8bc34a",
  "#4caf50",
  "#81c784",
  "#a5d6a7",
  "#c5e1a5",
  "#dce775",
  "#fff59d",
  "#aed581",
  "#7cb342",
  "#558b2f",
  "#33691e",
  "#76ff03",
  "#64dd17",
  "#00c853",
  "#00e676",
];

const GlobalViewPanel = ({ selectedListings, darkMode, onClose }) => {
  const { t } = useTranslation();
  const [isClosing, setIsClosing] = useState(false);

  // Theme-aware cursor color for bar charts
  const cursorStyle = {
    fill: darkMode ? "rgba(102, 157, 46, 0.25)" : "rgba(102, 157, 46, 0.12)",
  };

  // Threshold for switching legend layout
  const LEGEND_THRESHOLD = 8;

  // Get legend props based on item count
  const getLegendProps = (itemCount) => {
    if (itemCount > LEGEND_THRESHOLD) {
      // Many items: horizontal at bottom, wrapping
      return {
        wrapperStyle: {
          fontSize: "10px",
          paddingTop: "12px",
        },
        layout: "horizontal",
        align: "center",
        verticalAlign: "bottom",
      };
    }
    // Few items: vertical on right
    return {
      wrapperStyle: { fontSize: "11px" },
      layout: "vertical",
      align: "right",
      verticalAlign: "middle",
    };
  };

  // Get chart height based on legend position
  const getChartHeight = (itemCount) => {
    return itemCount > LEGEND_THRESHOLD ? 280 : 220;
  };

  // Get pie radius based on legend position
  const getPieRadius = (itemCount) => {
    return itemCount > LEGEND_THRESHOLD
      ? { outer: 80, inner: 40 }
      : { outer: 70, inner: 35 };
  };

  const stats = useMemo(() => {
    // Material distribution
    const materialCount = {};
    selectedListings.forEach((listing) => {
      materialCount[listing.material] =
        (materialCount[listing.material] || 0) + 1;
    });
    const materialDistribution = Object.entries(materialCount)
      .map(([name, value]) => ({ name: t(name), value }))
      .sort((a, b) => b.value - a.value); // Sort by count descending

    // Category distribution
    const categoryCount = {};
    selectedListings.forEach((listing) => {
      if (listing.material_category) {
        categoryCount[listing.material_category] =
          (categoryCount[listing.material_category] || 0) + 1;
      }
    });
    const categoryDistribution = Object.entries(categoryCount)
      .map(([name, value]) => ({ name: t(name), value }))
      .sort((a, b) => b.value - a.value);

    // Supplier distribution
    const supplierCount = {};
    selectedListings.forEach((listing) => {
      if (listing.company_id) {
        const supplierName =
          listing.company_name || `Supplier ${listing.company_id}`;
        supplierCount[supplierName] = (supplierCount[supplierName] || 0) + 1;
      }
    });
    const supplierDistribution = Object.entries(supplierCount)
      .map(([name, value]) => ({ name, value }))
      .sort((a, b) => b.value - a.value);

    // City distribution
    const cityCount = {};
    selectedListings.forEach((listing) => {
      if (listing.city) {
        cityCount[listing.city] = (cityCount[listing.city] || 0) + 1;
      }
    });
    const cityDistribution = Object.entries(cityCount)
      .map(([name, value]) => ({ name, value }))
      .sort((a, b) => b.value - a.value);

    // Quantity status
    const quantityByMaterial = {};
    selectedListings.forEach((listing) => {
      if (!quantityByMaterial[listing.material]) {
        quantityByMaterial[listing.material] = { available: 0, claimed: 0 };
      }
      quantityByMaterial[listing.material].available += listing.remaining_qty;
      quantityByMaterial[listing.material].claimed +=
        listing.quantity - listing.remaining_qty;
    });
    const quantityStatus = Object.entries(quantityByMaterial).map(
      ([material, quantities]) => ({
        name: t(material),
        available: Math.round(quantities.available * 100) / 100,
        claimed: Math.round(quantities.claimed * 100) / 100,
      }),
    );

    // Cost analysis
    const costByMaterial = {};
    selectedListings.forEach((listing) => {
      if (!costByMaterial[listing.material]) {
        costByMaterial[listing.material] = 0;
      }
      costByMaterial[listing.material] += listing.cost * listing.quantity;
    });
    const costAnalysis = Object.entries(costByMaterial)
      .map(([material, totalCost]) => ({
        material: t(material),
        totalCost: Math.round(totalCost * 100) / 100,
      }))
      .sort((a, b) => b.totalCost - a.totalCost);

    // Timeline
    const dateCount = {};
    selectedListings.forEach((listing) => {
      const date = new Date(listing.date_posted).toLocaleDateString("en-US", {
        month: "short",
        day: "numeric",
        year: "numeric",
      });
      dateCount[date] = (dateCount[date] || 0) + 1;
    });
    const timeline = Object.entries(dateCount)
      .map(([date, count]) => ({ date, count }))
      .sort((a, b) => new Date(a.date) - new Date(b.date));

    // Summary
    const totalQuantity = selectedListings.reduce(
      (sum, l) => sum + l.quantity,
      0,
    );
    const totalAvailable = selectedListings.reduce(
      (sum, l) => sum + l.remaining_qty,
      0,
    );
    const totalValue = selectedListings.reduce(
      (sum, l) => sum + l.cost * l.quantity,
      0,
    );
    const avgAvailability =
      totalQuantity > 0 ? (totalAvailable / totalQuantity) * 100 : 0;

    const mostCommon =
      Object.entries(materialCount).length > 0
        ? Object.entries(materialCount).reduce((a, b) => (a[1] > b[1] ? a : b))
        : [t("N/A"), 0];

    const deadlines = selectedListings
      .map((l) => new Date(l.latest_pickup))
      .filter((d) => !isNaN(d));
    const earliestDeadline =
      deadlines.length > 0 ? new Date(Math.min(...deadlines)) : null;

    return {
      materialDistribution,
      categoryDistribution,
      supplierDistribution,
      cityDistribution,
      quantityStatus,
      costAnalysis,
      timeline,
      summary: {
        totalListings: selectedListings.length,
        totalQuantity: Math.round(totalQuantity * 100) / 100,
        totalAvailable: Math.round(totalAvailable * 100) / 100,
        avgAvailability: Math.round(avgAvailability * 100) / 100,
        totalValue: Math.round(totalValue * 100) / 100,
        mostCommon: t(mostCommon[0]),
        mostCommonCount: mostCommon[1],
        earliestDeadline: earliestDeadline
          ? earliestDeadline.toLocaleDateString("en-US", {
              month: "short",
              day: "numeric",
              year: "numeric",
            })
          : t("N/A"),
      },
    };
  }, [selectedListings, t]);

  const handleClose = () => {
    setIsClosing(true);
    setTimeout(() => onClose(), 300);
  };

  const axisColor = darkMode ? "#aaaaaa" : "#666666";
  const gridColor = darkMode ? "#444444" : "#e0e0e0";

  // Custom tooltip for bar/line charts
  const CustomTooltip = ({ active, payload, label }) => {
    if (active && payload && payload.length) {
      return (
        <div className="chart-tooltip">
          <p className="chart-tooltip-label">{label}</p>
          {payload.map((entry, index) => (
            <p key={index} style={{ color: entry.color }}>
              {entry.name}: {entry.value}
            </p>
          ))}
        </div>
      );
    }
    return null;
  };

  // Custom tooltip for pie charts
  const PieTooltip = ({ active, payload, distributionData }) => {
    if (active && payload && payload.length) {
      const data = payload[0];
      const total = distributionData.reduce((sum, item) => sum + item.value, 0);
      const percentage = ((data.value / total) * 100).toFixed(0);
      return (
        <div className="chart-tooltip">
          <p className="chart-tooltip-label">{data.name}</p>
          <p>
            {data.value} {data.value === 1 ? t("listing") : t("listings")} (
            {percentage}%)
          </p>
        </div>
      );
    }
    return null;
  };

  // Reusable Pie Chart component
  const DistributionPieChart = ({ data, title, icon, iconColor }) => {
    const legendProps = getLegendProps(data.length);
    const chartHeight = getChartHeight(data.length);
    const pieRadius = getPieRadius(data.length);

    return (
      <div className="chart-card">
        <div className="chart-card-header">
          <div className={`chart-card-icon ${iconColor || ""}`}>
            <FontAwesomeIcon icon={icon} />
          </div>
          <h5 className="chart-card-title">{title}</h5>
          {data.length > LEGEND_THRESHOLD && (
            <span className="chart-card-badge">{data.length} items</span>
          )}
        </div>
        <div className="chart-card-content">
          <ResponsiveContainer width="100%" height={chartHeight}>
            <PieChart>
              <Pie
                data={data}
                dataKey="value"
                nameKey="name"
                cx={data.length > LEGEND_THRESHOLD ? "50%" : "40%"}
                cy="50%"
                outerRadius={pieRadius.outer}
                innerRadius={pieRadius.inner}
              >
                {data.map((entry, index) => (
                  <Cell
                    key={`cell-${index}`}
                    fill={COLORS[index % COLORS.length]}
                  />
                ))}
              </Pie>
              <Tooltip
                content={({ active, payload }) => (
                  <PieTooltip
                    active={active}
                    payload={payload}
                    distributionData={data}
                  />
                )}
              />
              <Legend {...legendProps} />
            </PieChart>
          </ResponsiveContainer>
        </div>
      </div>
    );
  };

  // Summary cards config
  const summaryCards = [
    {
      value: stats.summary.totalQuantity.toLocaleString(),
      unit: t("lbs"),
      title: t("Total Quantity"),
      icon: faBox,
      color: "blue",
    },
    {
      value: stats.summary.totalAvailable.toLocaleString(),
      unit: t("lbs"),
      title: t("Available"),
      icon: faCheckCircle,
      color: "green",
    },
    {
      value: `${stats.summary.avgAvailability}%`,
      title: t("Availability"),
      icon: faPercent,
      color: "amber",
    },
    {
      value: `$${stats.summary.totalValue.toLocaleString()}`,
      title: t("Total Value"),
      icon: faDollarSign,
      color: "blue",
    },
    {
      value: stats.summary.mostCommon,
      subtitle: `${stats.summary.mostCommonCount} ${t("listings")}`,
      title: t("Most Common"),
      icon: faStar,
      color: "green",
    },
    {
      value: stats.summary.earliestDeadline,
      title: t("Earliest Deadline"),
      icon: faCalendarAlt,
      color: "red",
    },
  ];

  return (
    <Card
      variant="system"
      darkMode={darkMode}
      className={`detailed-view-panel global-view-panel ${
        isClosing ? "closing" : ""
      }`}
    >
      {/* Header */}
      <div className="panel-header">
        <div className="panel-header-left">
          <h3 className="panel-title">
            <span className="panel-title-icon analytics">
              <FontAwesomeIcon icon={faChartPie} />
            </span>
            {t("Global View")}
            <span className="panel-header-separator" />
            <span className="panel-subtitle">
              {t("Compare costs, quantities, and deadlines across the")}{" "}
              {stats.summary.totalListings}{" "}
              {t("selected listings to spot trends and optimize")}
            </span>
          </h3>
        </div>
        <button
          onClick={handleClose}
          className="close-button"
          aria-label="Close"
        >
          <FontAwesomeIcon icon={faTimes} />
        </button>
      </div>

      {/* Summary Statistics */}
      <div className="stats-grid">
        {summaryCards.map((card, index) => (
          <div key={index} className={`stat-card stat-card--${card.color}`}>
            <div className="stat-card-icon">
              <FontAwesomeIcon icon={card.icon} />
            </div>
            <div className="stat-card-content">
              <div className="stat-card-value">
                {card.value}
                {card.unit && (
                  <span className="stat-card-unit">{card.unit}</span>
                )}
              </div>
              <div className="stat-card-title">{card.title}</div>
              {card.subtitle && (
                <div className="stat-card-subtitle">{card.subtitle}</div>
              )}
            </div>
          </div>
        ))}
      </div>

      {/* Charts Section */}
      <div className="charts-section">
        {/* Distribution Charts - 2x2 Grid */}
        <div className="charts-grid">
          {/* Material Distribution */}
          <DistributionPieChart
            data={stats.materialDistribution}
            title={t("Material Distribution")}
            icon={faLayerGroup}
          />

          {/* Category Distribution */}
          {stats.categoryDistribution.length > 0 && (
            <DistributionPieChart
              data={stats.categoryDistribution}
              title={t("Category Distribution")}
              icon={faChartPie}
              iconColor="amber"
            />
          )}

          {/* Supplier Distribution */}
          {stats.supplierDistribution.length > 0 && (
            <DistributionPieChart
              data={stats.supplierDistribution}
              title={t("Supplier Distribution")}
              icon={faBuilding}
              iconColor="blue"
            />
          )}

          {/* City Distribution */}
          {stats.cityDistribution.length > 0 && (
            <DistributionPieChart
              data={stats.cityDistribution}
              title={t("City Distribution")}
              icon={faMapMarkerAlt}
              iconColor="red"
            />
          )}
        </div>

        {/* Quantity Status - Bar Chart */}
        <div className="chart-card chart-card--full">
          <div className="chart-card-header">
            <div className="chart-card-icon green">
              <FontAwesomeIcon icon={faChartBar} />
            </div>
            <h5 className="chart-card-title">
              {t("Quantity Status by Material (lbs)")}
            </h5>
          </div>
          <div className="chart-card-content">
            <ResponsiveContainer width="100%" height={280}>
              <BarChart data={stats.quantityStatus}>
                <CartesianGrid strokeDasharray="3 3" stroke={gridColor} />
                <XAxis dataKey="name" stroke={axisColor} fontSize={11} />
                <YAxis stroke={axisColor} fontSize={11} />
                <Tooltip content={<CustomTooltip />} cursor={cursorStyle} />
                <Legend wrapperStyle={{ fontSize: "12px" }} />
                <Bar
                  dataKey="available"
                  stackId="a"
                  fill="#669d2e"
                  name={t("Available")}
                  radius={[0, 0, 0, 0]}
                />
                <Bar
                  dataKey="claimed"
                  stackId="a"
                  fill="#ef4444"
                  name={t("Claimed")}
                  radius={[4, 4, 0, 0]}
                />
              </BarChart>
            </ResponsiveContainer>
          </div>
        </div>

        {/* Total Value - Bar Chart */}
        <div className="chart-card chart-card--full">
          <div className="chart-card-header">
            <div className="chart-card-icon">
              <FontAwesomeIcon icon={faDollarSign} />
            </div>
            <h5 className="chart-card-title">{t("Total Value by Material")}</h5>
          </div>
          <div className="chart-card-content">
            <ResponsiveContainer width="100%" height={280}>
              <BarChart data={stats.costAnalysis} layout="vertical">
                <CartesianGrid strokeDasharray="3 3" stroke={gridColor} />
                <XAxis type="number" stroke={axisColor} fontSize={11} />
                <YAxis
                  type="category"
                  dataKey="material"
                  stroke={axisColor}
                  fontSize={11}
                  width={100}
                />
                <Tooltip content={<CustomTooltip />} cursor={cursorStyle} />
                <Legend wrapperStyle={{ fontSize: "12px" }} />
                <Bar
                  dataKey="totalCost"
                  fill="#8bc34a"
                  name={t("Total Value ($)")}
                  radius={[0, 4, 4, 0]}
                />
              </BarChart>
            </ResponsiveContainer>
          </div>
        </div>

        {/* Posting Timeline - Line Chart */}
        <div className="chart-card chart-card--full">
          <div className="chart-card-header">
            <div className="chart-card-icon purple">
              <FontAwesomeIcon icon={faChartLine} />
            </div>
            <h5 className="chart-card-title">{t("Posting Timeline")}</h5>
          </div>
          <div className="chart-card-content">
            <ResponsiveContainer width="100%" height={280}>
              <LineChart data={stats.timeline}>
                <CartesianGrid strokeDasharray="3 3" stroke={gridColor} />
                <XAxis dataKey="date" stroke={axisColor} fontSize={11} />
                <YAxis stroke={axisColor} fontSize={11} />
                <Tooltip content={<CustomTooltip />} cursor={cursorStyle} />
                <Legend wrapperStyle={{ fontSize: "12px" }} />
                <Line
                  type="monotone"
                  dataKey="count"
                  stroke="#669d2e"
                  strokeWidth={3}
                  name={t("Listings Posted")}
                  dot={{ fill: "#669d2e", strokeWidth: 2, r: 4 }}
                  activeDot={{ r: 6, fill: "#4a7a22" }}
                />
              </LineChart>
            </ResponsiveContainer>
          </div>
        </div>
      </div>
    </Card>
  );
};

GlobalViewPanel.propTypes = {
  selectedListings: PropTypes.array.isRequired,
  darkMode: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
};

export default GlobalViewPanel;
